// SPDX-License-Identifier: FSL-1.1-Apache-2.0
// Copyright 2026 Amantus Machina GmbH

'use client';

import type { LucideIcon } from 'lucide-react';
import { Activity, ArrowUpRight, BarChart3, Bird, Check, LineChart, Radar, Sparkles, Users } from 'lucide-react';
import Link from 'next/link';
import { useId, useState, useTransition } from 'react';
import { NewsletterSignup } from '@/components/common/NewsletterSignup';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { createAuthClientAdapter } from '@/lib/auth/auth-client-adapter';
import { authClient } from '@/lib/auth-client';
const HERO_METRICS = [{
  value: '45s',
  label: 'to surface drifts'
}, {
  value: '3×',
  label: 'more actionable cues'
}, {
  value: '0 spam',
  label: 'in recommended follows'
}] as const;
const SOCIAL_PROOF = ['Audience strategists', 'Investor relations leads', 'Boutique consultants', 'Independent analysts'] as const;
type Highlight = {
  icon: LucideIcon;
  title: string;
  description: string;
};
const SOLUTION_PILLARS: Highlight[] = [{
  icon: BarChart3,
  title: 'Signal-grade analytics',
  description: 'Layer sentiment, cohort, and posting velocity on top of your captured timeline to see why the algorithm made each move.'
}, {
  icon: Users,
  title: 'Friendimizer automation',
  description: 'Queue precision follow and unfollow batches with human context so you keep high-signal voices and shed the rest.'
}, {
  icon: Sparkles,
  title: 'Live pulse alerts',
  description: 'Get proactive notices when bios, mutuals, or follower clusters shift—no more surprises during big launches.'
}];
const PROBLEM_POINTS = [{
  title: 'Your timeline drifts faster than you can triage',
  description: 'The home feed keeps prioritizing automation, engagement bait, and stale conversations while high-value voices go missing.'
}, {
  title: 'Manual spreadsheet audits steal your time',
  description: 'By the time you finish collecting snapshots, the algorithm has shifted again and the insights are already out of date.'
}, {
  title: 'You keep improvising without a playbook',
  description: 'You spend nights translating insights for everyone else while momentum slips and responses stay reactive.'
}] as const;
type WorkflowStep = {
  step: string;
  title: string;
  description: string;
  metric: string;
};
const WORKFLOW_STEPS: WorkflowStep[] = [{
  step: '01',
  title: 'Capture & fingerprint',
  description: 'Sweetistics ingests your home timeline continuously, fingerprinting every post, author, and interaction pattern.',
  metric: 'Every 90 seconds'
}, {
  step: '02',
  title: 'Explain the algorithm',
  description: 'Our AI summary layer translates raw volatility into plain-language narratives you can act on immediately and share when it helps.',
  metric: 'Narratives in <2 min'
}, {
  step: '03',
  title: 'Instrument the fix',
  description: 'Launch Friendimizer queues, automate list hygiene, and push pulse alerts so your timeline stays aligned with intent.',
  metric: 'Batch-ready actions'
}];
type Testimonial = {
  quote: string;
  author: string;
  role: string;
};
const TESTIMONIALS: Testimonial[] = [{
  quote: 'We used to export raw Twitter lists weekly. Now Sweetistics predicts when our investor audience shifts topics before it shows up in DMs.',
  author: 'Sasha Patel',
  role: 'Head of Comms, growth-stage fintech'
}, {
  quote: 'Friendimizer eliminated 40% of the fluff in our executive timelines. The insight briefings make leadership actually trust the channel again.',
  author: 'Marcus Green',
  role: 'Chief of Staff, Series B SaaS'
}];
const safeAuthClient = createAuthClientAdapter(authClient);
export default function HomePage() {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isPending, startTransition] = useTransition();
  const learnMoreSectionId = useId();
  const handleLogin = async () => {
    startTransition(() => {
      setIsAuthenticating(true);
    });
    try {
      await safeAuthClient.signInSocial({
        provider: 'twitter',
        callbackURL: '/timeline'
      });
    } catch {
      // Sign in errors are handled by the authClient internally
    } finally {
      startTransition(() => {
        setIsAuthenticating(false);
      });
    }
  };
  const isLoading = isAuthenticating || isPending;
  return <div className="min-h-screen bg-background text-foreground">
      <main>
        <section className="relative overflow-hidden pb-24 pt-24 sm:pt-28">
          <div className="absolute -top-32 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-primary/25 blur-3xl" />
          <div className="absolute right-[-120px] top-24 h-72 w-72 rounded-full bg-muted/40 blur-3xl dark:bg-primary/20" />
          <div className="container relative mx-auto max-w-6xl px-4">
            <div className="grid gap-12 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
              <div className="space-y-8">
                <Badge variant="outline" className="w-fit rounded-full border-primary/30 bg-primary/10 text-primary">
                  Command the X algorithm
                </Badge>
                <div className="space-y-6">
                  <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
                    Grow reach without losing your timeline&apos;s signal
                  </h1>
                  <p className="text-lg text-muted-foreground sm:text-xl">
                    Sweetistics is your command center on X. Capture your feed, decode the algorithm, and automate the
                    follow-through so every scroll advances the outcomes you care about most.
                  </p>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <Button onClick={() => {
                  handleLogin().catch((error: unknown) => {
                    console.error('Twitter login failed', error);
                  });
                }} size="lg" className="h-12 px-8 text-base font-semibold" disabled={isLoading}>
                    <Bird className="mr-3 h-5 w-5" />
                    {isLoading ? 'Connecting…' : 'Connect Twitter account'}
                  </Button>
                  <Button asChild variant="ghost" size="lg" className="h-12 px-6 text-base font-semibold">
                    <a href={`#${learnMoreSectionId}`} className="inline-flex items-center gap-2">
                      Explore the workflow
                      <ArrowUpRight className="h-5 w-5" />
                    </a>
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {HERO_METRICS.map(({
                  value,
                  label
                }) => <div key={label} className="min-w-0 rounded-2xl border border-border/50 bg-card/80 p-5 text-sm shadow-sm dark:bg-background/80">
                      <p className="text-2xl font-semibold text-foreground sm:text-3xl">{value}</p>
                      <p className="mt-1 text-muted-foreground">{label}</p>
                    </div>)}
                </div>
              </div>

              <div className="relative">
                <div className="absolute -top-8 right-6 hidden h-24 w-24 rounded-full bg-primary/20 blur-2xl lg:block" />
                <Card className="relative border-border/50 bg-card/90 shadow-2xl">
                  <CardHeader className="flex flex-col gap-2">
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>Live feed monitor</span>
                      <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                        <Activity className="h-3.5 w-3.5" />
                        Streaming
                      </div>
                    </div>
                    <CardTitle className="text-xl font-semibold">This hour&apos;s signal report</CardTitle>
                    <CardDescription>Fresh snapshots, ranked cohorts, and actions ready to deploy.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3 rounded-2xl border border-border/60 bg-card/80 p-4 text-sm">
                      <p className="font-semibold text-foreground">Friendimizer queue</p>
                      <div className="space-y-2 text-muted-foreground">
                        <p>
                          • Replace 6 automation-heavy accounts with verified operators your audience already follows.
                        </p>
                        <p>• Boost reach by engaging the 4 fastest-growing analysts surfaced in the last snapshot.</p>
                      </div>
                    </div>
                    <div className="space-y-3 rounded-2xl border border-border/60 bg-card/80 p-4 text-sm">
                      <p className="font-semibold text-foreground">Pulse alerts</p>
                      <div className="space-y-2 text-muted-foreground">
                        <p>• 3 investor relations leads refreshed bios with new product launch messaging.</p>
                        <p>• 2 competitor execs swapped outbound links—notify comms to review positioning.</p>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="flex flex-col gap-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <LineChart className="h-4 w-4 text-primary" />
                      <span>Volatility settling after early-morning trend spike.</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Radar className="h-4 w-4 text-primary" />
                      <span>Fresh cohorts ready for outreach campaigns.</span>
                    </div>
                  </CardFooter>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-border/40 bg-muted/20 py-12 ">
          <div className="container mx-auto max-w-5xl px-4">
            <div className="flex flex-col items-center justify-between gap-6 text-center sm:flex-row sm:text-left">
              <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Trusted by operators shaping conversations on X
              </p>
              <div className="grid gap-x-8 gap-y-2 text-sm text-muted-foreground sm:grid-cols-2">
                {SOCIAL_PROOF.map(label_0 => <span key={label_0} className="uppercase tracking-wide">
                    {label_0}
                  </span>)}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background py-20">
          <div className="container mx-auto max-w-5xl px-4">
            <div className="mx-auto max-w-3xl text-center space-y-4">
              <Badge variant="outline" className="rounded-full border-border/60">
                Why Sweetistics
              </Badge>
              <h2 className="text-3xl font-bold sm:text-4xl">You deserve more than screenshots and gut checks</h2>
              <p className="text-lg text-muted-foreground">
                Operator-grade clarity built for the velocity of modern X. We focus on the problems that keep you awake
                at night.
              </p>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {PROBLEM_POINTS.map(({
              title,
              description
            }) => <div key={title} className="rounded-3xl border border-border/50 bg-card/70 p-6 text-left shadow-sm">
                  <p className="text-base font-semibold text-foreground">{title}</p>
                  <p className="mt-3 text-sm text-muted-foreground">{description}</p>
                </div>)}
            </div>
          </div>
        </section>

        <section className="border-y border-border/40 bg-gradient-to-br from-background via-muted/25 to-background py-20 dark:via-background/50">
          <div className="container mx-auto max-w-6xl px-4">
            <div className="mx-auto max-w-2xl text-center space-y-4">
              <Badge variant="outline" className="rounded-full border-primary/40 bg-primary/10 text-primary">
                Everything in one command center
              </Badge>
              <h2 className="text-3xl font-bold sm:text-4xl">Three pillars that keep your timeline intentional</h2>
              <p className="text-lg text-muted-foreground">
                We combine deep analytics, guided automation, and real-time monitoring so you never compromise on signal
                quality.
              </p>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {SOLUTION_PILLARS.map(({
              icon: Icon,
              title: title_0,
              description: description_0
            }) => <Card key={title_0} className="border-border/40 bg-card/85 shadow-lg">
                  <CardHeader className="flex flex-col gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <CardTitle className="text-xl font-semibold">{title_0}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{description_0}</p>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </section>

        <section id={learnMoreSectionId} className="relative overflow-hidden border-y border-border/40 bg-gradient-to-br from-background via-primary/5 to-background py-20">
          <div className="absolute left-12 top-16 h-32 w-32 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute bottom-12 right-8 h-40 w-40 rounded-full bg-muted/40 blur-3xl" />
          <div className="container relative mx-auto max-w-6xl px-4">
            <div className="mx-auto max-w-3xl text-center space-y-4">
              <Badge variant="outline" className="rounded-full border-primary/40 bg-primary/10 text-primary">
                The Sweetistics workflow
              </Badge>
              <h2 className="text-3xl font-bold sm:text-4xl">Capture, explain, act—repeat</h2>
              <p className="text-lg text-muted-foreground">
                Combining data capture, algorithmic storytelling, and guided automation keeps you in control and
                proactive.
              </p>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {WORKFLOW_STEPS.map(({
              step,
              title: title_1,
              description: description_1,
              metric
            }) => <Card key={title_1} className="border-border/40 bg-card/80 shadow-lg">
                  <CardHeader className="flex flex-col gap-2">
                    <span className="text-sm font-semibold text-primary">Step {step}</span>
                    <CardTitle className="text-xl font-semibold">{title_1}</CardTitle>
                    <CardDescription>{metric}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{description_1}</p>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </section>

        <section className="bg-background py-20">
          <div className="container mx-auto max-w-6xl px-4">
            <div className="grid gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
              <div className="space-y-4">
                <Badge variant="outline" className="w-fit rounded-full border-primary/30 text-primary">
                  What you see
                </Badge>
                <h2 className="text-3xl font-bold sm:text-4xl">A living briefing you can execute immediately</h2>
                <p className="text-lg text-muted-foreground">
                  Replace slide decks with contextualized insights. Sweetistics blends AI narration with raw telemetry
                  so you can make confident calls in minutes.
                </p>
                <ul className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 text-primary" />
                    <span>Explain algorithm shifts in plain language with the supporting data one click away.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 text-primary" />
                    <span>
                      Line up a Friendimizer queue that matches exactly what you&apos;re aiming to accomplish.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 text-primary" />
                    <span>Trigger pulse alerts when anything important shifts—even while you sleep.</span>
                  </li>
                </ul>
              </div>
              <Card className="border-border/50 bg-card/90 shadow-2xl">
                <CardHeader className="flex flex-col gap-1">
                  <CardTitle className="text-xl font-semibold">Personal briefing preview</CardTitle>
                  <CardDescription>Generated directly from your latest timeline snapshot.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 text-sm text-muted-foreground">
                  <div className="rounded-2xl border border-border/50 bg-card/80 p-4">
                    <p className="text-sm font-semibold text-foreground">Narrative</p>
                    <p className="mt-2">
                      Engagement pivoted toward AI policy debate at 09:20. Your coverage is under-indexed—amplify vetted
                      commentators surfaced below.
                    </p>
                  </div>
                  <div className="rounded-2xl border border-border/50 bg-card/80 p-4">
                    <p className="text-sm font-semibold text-foreground">Key voices</p>
                    <p className="mt-2">
                      @alison-hsu (+12% velocity), @growthresearcher (+9%), and @spacebrief (+7%) are driving reach
                      within your target cohort.
                    </p>
                  </div>
                  <div className="rounded-2xl border border-border/50 bg-card/80 p-4">
                    <p className="text-sm font-semibold text-foreground">Immediate actions</p>
                    <ul className="mt-2 space-y-2">
                      <li>• Accept five Friendimizer additions to replace dormant automation accounts.</li>
                      <li>• Draft a pulse update with commentary-ready pull quotes.</li>
                      <li>• Schedule your outreach thread to ride today&apos;s velocity spike.</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="border-y border-border/40 bg-muted/20 py-20 ">
          <div className="container mx-auto max-w-5xl px-4">
            <div className="mx-auto max-w-2xl text-center space-y-4">
              <Badge variant="outline" className="rounded-full border-border/50">
                Operators already switching
              </Badge>
              <h2 className="text-3xl font-bold sm:text-4xl">They cut noise. They kept their reach.</h2>
            </div>
            <div className="mt-12 grid gap-8 md:grid-cols-2">
              {TESTIMONIALS.map(({
              quote,
              author,
              role
            }) => <Card key={author} className="border-border/40 bg-card/85 shadow-lg">
                  <CardContent className="space-y-4 p-8">
                    <Sparkles className="h-6 w-6 text-primary" />
                    <p className="text-lg font-medium text-foreground">“{quote}”</p>
                    <div className="text-sm text-muted-foreground">
                      <p className="font-semibold text-foreground">{author}</p>
                      <p>{role}</p>
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </section>

        <section className="bg-background py-20">
          <div className="container mx-auto max-w-5xl px-4">
            <div className="grid items-start gap-8 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
              <Card className="border-border/60 bg-card/80 shadow-xl">
                <CardHeader>
                  <Badge variant="outline" className="w-fit rounded-full border-success/40 text-success">
                    Included
                  </Badge>
                  <CardTitle className="text-2xl font-semibold">Essential workspace</CardTitle>
                  <CardDescription>
                    Validate Sweetistics with your live data, AI narratives, and light automation.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 text-primary" />
                    <span>Live timeline snapshots with velocity overlays</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 text-primary" />
                    <span>AI insight briefs (10 per month)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 text-primary" />
                    <span>Baseline Friendimizer recommendations</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 text-primary" />
                    <span>Standard data export options</span>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button asChild variant="secondary" className="w-full">
                    <Link href="/pricing">See pricing details</Link>
                  </Button>
                </CardFooter>
              </Card>
              <Card className="border-primary/50 bg-gradient-to-br from-primary/15 via-card to-background shadow-2xl">
                <CardHeader>
                  <Badge className="w-fit rounded-full bg-primary text-primary-foreground">Popular</Badge>
                  <CardTitle className="text-2xl font-semibold">Sweetistics Pro</CardTitle>
                  <CardDescription>All-access automation, deeper history, and premium support.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 text-primary" />
                    <span>Unlimited AI analysis and insight briefings</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 text-primary" />
                    <span>Friendimizer™ automation with bulk approvals</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 text-primary" />
                    <span>Pulse monitoring with webhook + API access</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 text-primary" />
                    <span>Extended history retention and cohort analytics</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 text-primary" />
                    <span>Priority engineering support and roadmap input</span>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button onClick={() => {
                  handleLogin().catch((error_0: unknown) => {
                    console.error('Twitter login failed', error_0);
                  });
                }} size="lg" className="w-full" disabled={isLoading}>
                    {isLoading ? 'Connecting…' : 'Start Pro pilot'}
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </div>
        </section>

        <section className="border-t border-border/40 bg-gradient-to-br from-background via-primary/5 to-background py-20">
          <div className="container mx-auto max-w-4xl px-4">
            <div className="rounded-3xl border border-border/60 bg-card/80 px-8 py-10 shadow-2xl dark:bg-background/70">
              <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                <div className="space-y-3 max-w-xl">
                  <Badge variant="outline" className="w-fit rounded-full border-primary/30 text-primary">
                    Stay ahead of platform shifts
                  </Badge>
                  <h3 className="text-2xl font-semibold text-foreground">Join the Sweetistics field notes</h3>
                  <p className="text-sm text-muted-foreground">
                    Weekly debriefs on growth experiments, X algorithm updates, and exactly how top operators are using
                    Sweetistics to win.
                  </p>
                </div>
                <NewsletterSignup variant="inline" className="w-full" />
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>;
}